import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    RadioGroup,
    FormControlLabel,
    Radio,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import orderService from '../services/orderService';
import { UserLink } from '../utils/userLink';

// "2026-05-03 11:08" → "05-03 11"
const formatOrderDate = (s) => {
    if (!s) return '';
    const m = String(s).match(/^\d{4}-(\d{2}-\d{2})[ T](\d{2})/);
    return m ? `${m[1]} ${m[2]}` : s;
};

const Orders = () => {
    const navigate = useNavigate(); // orderItem navigate 정의
    // 주문 목록 및 상태
    const [orders, setOrders] = useState([]);
    const [, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [orderStatus, setOrderStatus] = useState(1); // 기본 상태: 주문완료

    // 송장번호 입력 Dialog 관련 상태
    const [open, setOpen] = useState(false);
    const [selectedOrderItemId, setSelectedOrderItemId] = useState(null);
    const [trackingNumber, setTrackingNumber] = useState('');
    const [selectedCode, setSelectedCode] = useState('00');

    // 무통장입금 확인 Dialog 관련 상태
    const [depositOpen, setDepositOpen] = useState(false);
    const [depositDate, setDepositDate] = useState('');
    const [depositorName, setDepositorName] = useState('');

    // 스마트택배 열기
    const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
    const [trackingInvoice, setTrackingInvoice] = useState('');
    const [iframeKey, setIframeKey] = useState(0); // iframe 새로고침용

    // 송장번호 클릭 시 Dialog 오픈
    const handleTrackingPopup = (invoiceNo) => {
        const onlyNumbers = invoiceNo.replace(/\D/g, '');
        if (!invoiceNo || onlyNumbers.length !== 12) return;
        setTrackingInvoice(onlyNumbers);
        setTrackingDialogOpen(true);
        setIframeKey(prev => prev + 1); // iframe 강제 리로드
    };

    const handleChange = (code) => {
        console.log(code);
        setSelectedCode(code);
    };

    // 주문 상태에 따른 라벨 매핑
    const statusLabels = {
        // 0: '무통장입금',
        1: '주문완료',
        2: '상품준비중',
        3: '배송중',
        4: '배송완료',
        5: '반품요청',
        6: '반품완료',
        7: '교환요청',
        8: '교환완료',
        9: '주문취소',
    };

    const statusButtonLabels = {
        // 0: '무통장입금 확인',
        1: '상품준비중 처리',
        2: '배송중처리',
        3: '배송완료처리',
        // 4: '-',
        // 5: '-',
        // 6: '-',
        // 7: '-',
        // 8: '-',
    };

    const courierData = [
        { code: '00', name: '직접수령' },
        { code: '01', name: '우체국택배' },
        { code: '04', name: 'CJ대한통운' },
        { code: '05', name: '한진택배' },
        { code: '06', name: '로젠택배' },
        { code: '08', name: '롯데택배' },
        { code: '23', name: '경동택배' },
        // ... (나머지 택배사 데이터)
    ];

    // 주문 목록 조회
    const fetchOrders = async (status) => {
        setLoading(true);
        try {
            const response = await orderService.fetchOrders(status);
            setOrders(response.data);
        } catch (error) {
            setErrorMessage('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    // 중복 호출 방지용 ref
    const initialized = useRef(false);

    // 첫 렌더링 및 orderStatus 변경 시 주문 목록 재조회
    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            fetchOrders(orderStatus);
            return;
        }
        fetchOrders(orderStatus);
    }, [orderStatus]);

    const handleRefresh = () => {
        fetchOrders(orderStatus);
    };

    // === 송장번호 입력 Dialog 처리 ===
    const handleOpen = (orderItemId) => {
        setSelectedOrderItemId(orderItemId);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedOrderItemId(null);
        setTrackingNumber('');
    };

    const handleSaveTrackingNumber = async () => {
        // console.log(`Order ID: ${selectedOrderItemId}, Tracking Number: ${trackingNumber}, Delivery Com: ${selectedCode}`);
        try {
            await orderService.shippingOrder(selectedOrderItemId, trackingNumber, selectedCode);
            handleRefresh();
        } catch (err) {
            console.error(err);
            setErrorMessage('송장번호 저장 중 오류가 발생했습니다.');
        } finally {
            handleClose();
        }
    };

    // === 무통장입금 확인 Dialog 처리 ===
    const handleBankCheck = (orderItemId) => {
        setSelectedOrderItemId(orderItemId);
        setDepositOpen(true);
    };

    const handleDepositClose = () => {
        setDepositOpen(false);
        setSelectedOrderItemId(null);
        setDepositDate('');
        setDepositorName('');
    };

    const handleSaveBankDeposit = async () => {
        console.log(
            `Order ID: ${selectedOrderItemId}, Deposit Date: ${depositDate}, Depositor Name: ${depositorName}`
        );
        try {
            // 임시 API 호출 (orderService.confirmBankDeposit 는 임시 API 함수로 가정)
            await orderService.confirmBankDeposit(selectedOrderItemId, depositDate, depositorName);
            handleRefresh();
        } catch (err) {
            console.error(err);
            setErrorMessage('무통장입금 확인 처리 중 오류가 발생했습니다.');
        } finally {
            handleDepositClose();
        }
    };

    // 기타 주문 상태 변경 함수들
    const handleSaveOrderStatus = async (orderItemId, order_status) => {
        try {
            await orderService.changeOrderStatus(orderItemId, order_status);
            handleRefresh();
        } catch (err) {
            console.error(err);
            setErrorMessage('주문상태 변경 중 오류가 발생했습니다.');
        } finally {
            handleClose();
        }
    };

    const handleDeliveryComplete = async (orderItemId) => {
        try {
            await orderService.deliveryComplete(orderItemId);
            handleRefresh();
        } catch (err) {
            console.error(err);
            setErrorMessage('배송완료 처리 중 오류가 발생했습니다.');
        } finally {
            handleClose();
        }
    };

    const handlePreparation = (orderItemId) => {
        handleSaveOrderStatus(orderItemId, 2);
    };

    const handleShipping = (orderItemId) => {
        handleSaveOrderStatus(orderItemId, 4);
    };

    const handleReturnRequest = (orderItemId) => {
        handleSaveOrderStatus(orderItemId, 6);
    };

    const handleReturnComplete = (orderItemId) => {
        console.log(`반품완료 처리: ${orderItemId}`);
    };

    const handleExchangeRequest = (orderItemId) => {
        handleSaveOrderStatus(orderItemId, /* 적절한 상태 코드 입력 */);
    };

    const handleExchangeComplete = (orderItemId) => {
        console.log(`교환완료 처리: ${orderItemId}`);
    };

    // 주문 상태에 따른 처리 액션 함수
    const handleAction = (orderItemId, status) => {
        switch (status) {
            // case 0:
            //     handleBankCheck(orderItemId); //입금확인중
            //     break;
            case 1:
                handlePreparation(orderItemId); //결제완료
                break;
            case 2:
                handleOpen(orderItemId); //상품준비중
                break;
            case 3:
                handleDeliveryComplete(orderItemId); //배송중
                break;
            case 4:
                // handleDeliveryComplete(orderItemId); //배송완료
                break;
            case 5:
                handleReturnRequest(orderItemId); //반품요청
                break;
            case 6:
                handleReturnComplete(orderItemId); //반품완료
                break;
            case 7:
                handleExchangeRequest(orderItemId); //교환요청
                break;
            case 8:
                handleExchangeComplete(orderItemId); //교환완료
                break;
            case 9: // 주문취소
                break;
            default:
                console.log(`알 수 없는 상태 or 주문대기 상태: ${orderItemId}`);
                break;
        }
    };

    const exportToExcel = (orders) => {
        const worksheet = XLSX.utils.json_to_sheet(
            orders.map((order) => ({
                'Order ID': order.order_id,
                'Order Item ID': order.order_item_id,
                '주문자명': order.order_user_name,
                '상품명': order.name,
                '주문수량': order.order_qty,
                '우편번호': order.zipcode,
                '주소': order.address,
                '상세주소': order.address_detail,
                '수령인': order.receiver_name,
                '전화번호': order.phone_number,
                '주문일자': order.order_date,
                '주문요청사항': order.delivery_comment,
                "사용포인트": order.use_points,
                '송장번호': order.invoice_no,
                '주문상태': statusLabels[order.order_status] || '알 수 없는 상태',
            }))
        );

        const workbook = XLSX.utils.book_new();
        worksheet['!cols'] = [
            { wch: 15 },
            { wch: 15 },
            { wch: 15 },
            { wch: 25 },
            { wch: 15 },
            { wch: 10 },
            { wch: 50 },
            { wch: 30 },
            { wch: 15 },
            { wch: 20 },
            { wch: 20 },
            { wch: 50 },
            { wch: 20 },
            { wch: 20 },
        ];
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
        XLSX.writeFile(workbook, 'Orders.xlsx');
    };

    const handleDownload = () => {
        exportToExcel(orders);
    };

    return (
        <Container maxWidth={false} sx={{ width: "100%", pb: 8 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Typography variant="h4" gutterBottom>
                    주문배송처리
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => navigate('/orderItem')}
                    >
                        현장주문등록
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleDownload}
                    >
                        엑셀 다운로드
                    </Button>
                </Box>
            </Box>
            {errorMessage && (
                <Typography variant="body2" color="error" gutterBottom>
                    {errorMessage}
                </Typography>
            )}

            {/* 주문 상태 선택 */}
            <RadioGroup
                row
                value={orderStatus}
                onChange={(e) => setOrderStatus(Number(e.target.value))}
            >
                {/*<FormControlLabel value={0} control={<Radio />} label="무통장입금" />*/}
                <FormControlLabel value={1} control={<Radio />} label="결제완료" />
                <FormControlLabel value={2} control={<Radio />} label="상품준비중" />
                <FormControlLabel value={3} control={<Radio />} label="배송중" />
                <FormControlLabel value={4} control={<Radio />} label="배송완료" />
                {/*<FormControlLabel value={5} control={<Radio />} label="반품요청" />*/}
                {/*<FormControlLabel value={6} control={<Radio />} label="반품완료" />*/}
                {/*<FormControlLabel value={7} control={<Radio />} label="교환요청" />*/}
                {/*<FormControlLabel value={8} control={<Radio />} label="교환완료" />*/}
                <FormControlLabel value={9} control={<Radio />} label="주문취소" />
            </RadioGroup>

            {/* 주문 목록 테이블 */}
            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 90 }}>주문일시</TableCell>
                            <TableCell sx={{ width: 130 }}>주문자 / 수령</TableCell>
                            <TableCell>상품 / 수량</TableCell>
                            <TableCell>주소 / 배송요청</TableCell>
                            <TableCell sx={{ width: 140 }}>수령인 / 전화</TableCell>
                            <TableCell sx={{ width: 200 }}>송장 / 주문번호</TableCell>
                            <TableCell sx={{ width: 90 }}>상태</TableCell>
                            <TableCell sx={{ width: 110 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orders.length > 0 ? (
                            orders.map((order) => (
                                <TableRow key={order.order_item_id} sx={{ verticalAlign: 'top' }}>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                        {formatOrderDate(order.order_date)}
                                    </TableCell>
                                    <TableCell>
                                        <UserLink userId={order.user_id}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {order.order_user_name}
                                            </Typography>
                                        </UserLink>
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            {order.receipt_method}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{order.name}</Typography>
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            수량 {order.order_qty}
                                            {order.use_points ? ` · 포인트 ${Number(order.use_points).toLocaleString()}` : ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            [{order.zipcode}] {order.address} {order.address_detail}
                                        </Typography>
                                        {order.delivery_comment && (
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                display="block"
                                                sx={{ fontStyle: 'italic' }}
                                            >
                                                💬 {order.delivery_comment}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{order.receiver_name}</Typography>
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            {order.phone_number}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {order.invoice_no ? (
                                            <Typography
                                                variant="body2"
                                                onClick={() => handleTrackingPopup(order.invoice_no)}
                                                sx={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                                            >
                                                {order.invoice_no}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">-</Typography>
                                        )}
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            display="block"
                                            sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}
                                        >
                                            {order.order_id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {statusLabels[order.order_status] || '알 수 없는 상태'}
                                    </TableCell>
                                    <TableCell>
                                        {statusButtonLabels[order.order_status] && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleAction(order.order_item_id, order.order_status)}
                                            >
                                                {statusButtonLabels[order.order_status]}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    No orders found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* 송장번호 입력 Dialog */}
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>송장번호 입력</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Order Item ID: {selectedOrderItemId}
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="송장번호"
                        type="text"
                        fullWidth
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                    />
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="courier-select-label">택배사</InputLabel>
                        <Select
                            labelId="courier-select-label"
                            value={selectedCode}
                            onChange={(e) => handleChange(e.target.value)}
                            fullWidth
                        >
                            <MenuItem value="">
                                <em>Choose a courier</em>
                            </MenuItem>
                            {courierData.map((courier) => (
                                <MenuItem key={courier.code} value={courier.code}>
                                    {courier.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {selectedCode && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Selected Courier:{' '}
                            <strong>{courierData.find((c) => c.code === selectedCode)?.name}</strong>
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="secondary">
                        취소
                    </Button>
                    <Button onClick={handleSaveTrackingNumber} color="primary">
                        저장
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 무통장입금 확인 Dialog */}
            <Dialog open={depositOpen} onClose={handleDepositClose}>
                <DialogTitle>무통장입금 확인</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Order ID: {selectedOrderItemId}
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="입금일"
                        type="date"
                        fullWidth
                        value={depositDate}
                        onChange={(e) => setDepositDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="입금자명"
                        type="text"
                        fullWidth
                        value={depositorName}
                        onChange={(e) => setDepositorName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDepositClose} color="secondary">
                        취소
                    </Button>
                    <Button onClick={handleSaveBankDeposit} color="primary">
                        확인
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 스마트택배 Dialog */}
            <Dialog
              open={trackingDialogOpen}
              onClose={() => setTrackingDialogOpen(false)}
              maxWidth={false}
              PaperProps={{
                style: { width: 440, maxWidth: 'none' }
              }}
            >
              <DialogContent
                style={{
                  padding: 0,
                  overflow: 'auto',
                  scrollbarWidth: 'none', // Firefox
                  msOverflowStyle: 'none', // IE, Edge
                }}
                sx={{
                  '&::-webkit-scrollbar': { display: 'none' }, // Chrome, Safari
                }}
              >
                <iframe
                  key={iframeKey}
                  src={`https://info.sweettracker.co.kr/tracking/5?t_key=oVLrYDASS8feRuWcHheXdg&t_code=08&t_invoice=${trackingInvoice}`}
                  title="SweetTracker"
                  width="435px"
                  height="600px"
                  style={{
                    border: '1px solid #ccc',
                    display: 'block',
                    overflow: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                  // iframe 내부 스크롤바는 외부에서 완전히 제어 불가
                />
              </DialogContent>
            </Dialog>
        </Container>
    );
};

export default Orders;
